import React from 'react';
import {Box, Typography, IconButton, Tooltip} from '@mui/material';
import ArrButton from '../../../../common/arr-button/ArrButton';
import './ManageIndicesHeader.scss';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {Trans} from 'react-i18next';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';

export type ManageIndicesHeaderButtonDetail = {
  title?: string;
  onClick: () => void;
  icon?: React.ReactNode;
  iconOnly?: boolean;
  ariaLabel?: string;
  tooltip?: string;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium' | 'large';
  dataTestId: string;
};

export interface ManageIndicesHeaderProps {
  title: string;
  tooltipContent: string;
  buttonDetails: ManageIndicesHeaderButtonDetail[];
  titleId?: string;
}

const ManageIndicesHeader: React.FC<ManageIndicesHeaderProps> = ({
  title,
  tooltipContent,
  buttonDetails,
  titleId,
}) => {
  return (
    <Box
      className="manage-indices-header-container"
      component="header"
      aria-label={title}
    >
      <Box className="manage-indices-header-title-section">
        <Typography
          id={titleId}
          className="typo-text headline3_size manage-indices-header-title"
          component="h1"
          data-testid="manage-indices-header-title"
        >
          {title}
          <ArrTooltipWrapper
            title={title}
            trigger={
              <InfoIcon
                className="info-icon onlySvgIcon"
                aria-hidden="true"
                focusable="false"
                data-testid="manage-indices-header-tooltip"
              />
            }
          >
            <Trans
              i18nKey={tooltipContent}
              components={{
                br: <br data-testid="manage-indices-header-br" />,
                strong: <strong data-testid="manage-indices-header-strong" />,
                p: <p data-testid="manage-indices-header-p" />,
              }}
            />
          </ArrTooltipWrapper>
        </Typography>
      </Box>
      <Box
        className="manage-indices-header-actions"
        aria-label="Header actions"
        role="group"
      >
        {buttonDetails.map((btn, index) => {
          const key = `${btn.title ?? btn.ariaLabel ?? 'action'}-${index}`;
          const accessibleName =
            btn.ariaLabel ?? btn.title ?? btn.tooltip ?? 'action';

          if (btn.iconOnly) {
            const iconButton = (
              <Box
                className={`icon-button-container${
                  btn.disabled ? ' is-disabled' : ''
                }`}
              >
                <IconButton
                  onClick={btn.onClick}
                  disabled={btn.disabled}
                  aria-label={accessibleName}
                  data-testid={`manage-${btn.dataTestId}`}
                  className="manage-indices-header-icon-button"
                  size="medium"
                  disableRipple
                >
                  <span
                    className="download-icon"
                    data-testid={`manage-indices-header-icon-button-${index}`}
                  >
                    {btn.icon}
                  </span>
                </IconButton>
              </Box>
            );

            return btn.tooltip ? (
              <Tooltip key={key} title={btn.tooltip} describeChild>
                <span
                  style={{display: 'inline-flex'}}
                  data-testid={`manage-indices-header-icon-span-${index}`}
                >
                  {iconButton}
                </span>
              </Tooltip>
            ) : (
              <React.Fragment key={key}>{iconButton}</React.Fragment>
            );
          }

          return (
            <ArrButton
              key={key}
              aria-label={accessibleName}
              variant={btn.variant ?? (index === 0 ? 'secondary' : 'primary')}
              size={btn.size ?? 'medium'}
              onClick={btn.onClick}
              disabled={btn.disabled}
              className="manage-indices-header-action-button"
              dataTestId={`manage-indices-header-button-${btn.dataTestId}`}
            >
              <Typography
                className="btn-text"
                data-testid={`manage-indices-header-button-text-${btn.dataTestId}`}
              >
                {btn.icon ? (
                  <span
                    data-testid={`manage-indices-icon-button-${btn.dataTestId}`}
                  >
                    {btn.icon}
                  </span>
                ) : null}
                {btn.title}
              </Typography>
            </ArrButton>
          );
        })}
      </Box>
    </Box>
  );
};

export default ManageIndicesHeader;
